import { AccountDeletionResult, ServiceError, User } from 'model';
import { BaseApi } from './BaseAPI';

const baseURL = 'users/';

export const UserApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		getProfile: builder.query<User, string>({
			query: (id: string) => ({
				url: `${baseURL}profile/${id}`
			}),
			providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
		}),
		getAdminProfile: builder.query<User, string>({
			query: (id: string) => ({
				url: `${baseURL}adminprofile/${id}`
			}),
			providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
		}),
		getOwnProfile: builder.query<User, string>({
			// Though ownprofile takes an id, it is ignored, and the token is used instead, we include it as a parm
			// for invalidation/tags
			query: (id: string) => ({
				url: `${baseURL}ownprofile/${id}`
			}),
			providesTags: (...arg) => [{ type: 'User', id: arg[2] }]
		}),

		searchUser: builder.query<User[], string>({
			query: (search: string) => ({
				url: `${baseURL}search/${search}`
			})
		}),
		updateUser: builder.mutation<User, { user: User }>({
			query: ({ user }) => ({
				url: `${baseURL}update`,
				method: 'PUT',
				body: { user }
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].user._id }]
		}),

		sendUserFeedback: builder.mutation<string, string>({
			query: (feedback: string) => ({
				url: `${baseURL}sendUserFeedback`,
				method: 'POST',
				body: { feedback }
			})
		}),
		suspendUser: builder.mutation<User, { userid: User['_id']; suspendedUntil: string }>({
			query: ({ userid, suspendedUntil }) => ({
				url: `${baseURL}suspendUser/${userid}/${suspendedUntil}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].userid }]
		}),

		unSuspendUser: builder.mutation<User, { userid: User['_id'] }>({
			query: ({ userid }) => ({
				url: `${baseURL}unSuspendUser/${userid}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].userid }]
		}),

		setActive: builder.mutation<User, { userid: User['_id']; active: boolean }>({
			query: ({ userid, active }) => ({
				url: `${baseURL}setActive/${userid}/${active}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].userid }]
		}),

		updateOwnUserDescription: builder.mutation<User, { user: User }>({
			query: ({ user }) => ({
				url: `${baseURL}updateDescription`,
				method: 'PUT',
				body: { user }
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].user._id }]
		}),

		updateFeedPreferences: builder.mutation<User, { user: User }>({
			query: ({ user }) => ({
				url: `${baseURL}updateFeedPreferences`,
				method: 'PUT',
				body: { user }
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].user._id }, { type: 'Post' }]
		}),

		updateTopicPreferences: builder.mutation<User, { user: User }>({
			query: ({ user }) => ({
				url: `${baseURL}updateTopicPreferences`,
				method: 'PUT',
				body: { user }
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].user._id }, { type: 'Post' }]
		}),

		updateNotificationPreferences: builder.mutation<User, { user: User }>({
			query: ({ user }) => ({
				url: `${baseURL}updateNotificationPreferences`,
				method: 'PUT',
				body: { user }
			}),

			invalidatesTags: (...args) => [{ type: 'User', id: args[2].user._id }]
		}),

		updateEngagementPreferences: builder.mutation<User, { user: User }>({
			query: ({ user }) => ({
				url: `${baseURL}updateEngagementPreferences`,
				method: 'PUT',
				body: { user }
			}),

			invalidatesTags: (...args) => [{ type: 'User', id: args[2].user._id }]
		}),

		acknowledgeTermsOfService: builder.mutation<User, number>({
			query: version => ({
				url: `${baseURL}termsOfService/${version}`,
				method: 'PUT'
			}),
			invalidatesTags: result => [{ type: 'User', id: result?._id }]
		}),

		addProfileImage: builder.mutation<User, { document: User; data: ImageData }>({
			query: ({ document, data }) => ({
				url: `${baseURL}profileImage/${document._id}`,
				method: 'PUT',
				body: data
			}),

			invalidatesTags: (...args) => [{ type: 'User', id: args[2].document._id }, { type: 'Post' }]
		}),

		addExperience: builder.mutation<User, { experience: string }>({
			query: ({ experience }) => ({
				url: `${baseURL}experience/${experience}`,
				method: 'PUT'
			}),
			invalidatesTags: () => [{ type: 'User' }]
		}),

		sendResetPasswordEmail: builder.mutation<string, { email: string }>({
			query: ({ email }) => ({
				url: `${baseURL}resetPasswordEmail/${email}/`,
				method: 'POST',
				body: {}
			})
		}),

		getUsersByDay: builder.query<
			{ date: string; total: number }[],
			{ startDate: string; endDate: string; noAnon: boolean }
		>({
			query: ({ startDate, endDate, noAnon }) => ({
				url: `${baseURL}admin/usersByDay/${startDate}/${endDate}/${noAnon}/`,
				method: 'GET'
			})
		}),

		deleteAccount: builder.mutation<AccountDeletionResult | ServiceError, { userid: User['_id'] }>({
			query: () => ({
				url: `${baseURL}deleteAccount/`,
				method: 'GET'
			}),
			invalidatesTags: (...args) => [{ type: 'User', id: args[2].userid }]
		})
	})
});

export const {
	useGetProfileQuery,
	useGetAdminProfileQuery,
	useLazyGetProfileQuery,
	useLazySearchUserQuery,
	useUpdateUserMutation,
	useGetOwnProfileQuery,
	useUpdateOwnUserDescriptionMutation,
	useUpdateFeedPreferencesMutation,
	useAddProfileImageMutation,
	useSuspendUserMutation,
	useUnSuspendUserMutation,
	useAcknowledgeTermsOfServiceMutation,
	useSetActiveMutation,
	useSendUserFeedbackMutation,
	useAddExperienceMutation,
	useSendResetPasswordEmailMutation,
	useUpdateNotificationPreferencesMutation,
	useUpdateEngagementPreferencesMutation,
	useUpdateTopicPreferencesMutation,
	useGetUsersByDayQuery,
	useSearchUserQuery,
	useDeleteAccountMutation
} = UserApi;
